import React, { useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Card,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Support as SupportIcon,
  Lock as LockIcon,
  Facebook,
  Instagram,
  WhatsApp,
  QrCode2 as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
  SignalCellularConnectedNoInternet2Bar as SignalWeakIcon,
  SignalCellularConnectedNoInternet0Bar as SignalOffIcon,
  SignalCellular4Bar as SignalStrongIcon,
} from "@mui/icons-material";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import PrivacyModal from "../../components/PrivacyModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    height: "calc(100vh - 144px)",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.background.default,
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.divider,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.action.hover,
    },
  },
  connectionCard: {
    height: "100%",
    backgroundColor: "transparent",
    borderRadius: theme.shape.borderRadius,
    transition: "all 0.3s ease",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    color: theme.palette.text.primary,
    "&:first-of-type": {
      marginTop: 0,
    },
  },
  cardHeader: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "transparent",
  },
  cardContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: "transparent",
  },
  statusChip: {
    borderRadius: "16px",
    fontWeight: 500,
    textTransform: "capitalize",
  },
  actionButton: {
    minWidth: "120px",
  },
  channelIcon: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
  },
  importProgress: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
}));

// Componente para renderizar cada conexão em formato de card
const ConnectionCard = ({
  connection,
  onEdit,
  onDelete,
  onQrCode,
  onPrivacy,
  onDisconnect,
  onStartSession,
  onRequestNewQrCode,
  onClosedImported,
  onDoubleClick,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const { user } = useContext(AuthContext);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "CONNECTED":
        return theme.palette.success;
      case "DISCONNECTED":
      case "TIMEOUT":
      case "PAIRING":
        return theme.palette.error;
      case "qrcode":
        return theme.palette.warning;
      default:
        return theme.palette.grey;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "CONNECTED":
        return <SignalStrongIcon sx={{ color: getStatusColor(status).main }} />;
      case "DISCONNECTED":
        return <SignalOffIcon sx={{ color: getStatusColor(status).main }} />;
      case "TIMEOUT":
      case "PAIRING":
        return <SignalWeakIcon sx={{ color: getStatusColor(status).main }} />;
      case "qrcode":
        return <QrCodeIcon sx={{ color: getStatusColor(status).main }} />;
      default:
        return null;
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "whatsapp":
        return <WhatsApp style={{ color: "#25D366" }} className={classes.channelIcon} />;
      case "facebook":
        return <Facebook style={{ color: "#3b5998" }} className={classes.channelIcon} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c" }} className={classes.channelIcon} />;
      default:
        return null;
    }
  };

  return (
    <Card
      className={classes.connectionCard}
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        "& .MuiCardContent-root": { backgroundColor: "transparent" },
      }}
      onDoubleClick={() => onDoubleClick(connection)}
    >
      <Box className={classes.cardHeader}>
        <Stack direction="row" alignItems="center">
          {getChannelIcon(connection.channel)}
          <Typography variant="subtitle1" fontWeight={500}>
            {connection.name}
          </Typography>
        </Stack>
        <Can
          role={user.profile}
          perform="connections-page:addConnection"
          yes={() => (
            <IconButton size="small" onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
          )}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onEdit();
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Editar
          </MenuItem>
          {connection.status === "CONNECTED" && connection.channel === "whatsapp" && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onPrivacy();
              }}
            >
              <LockIcon fontSize="small" sx={{ mr: 1 }} />
              Privacidade
            </MenuItem>
          )}
          {/* <MenuItem
            onClick={() => {
              handleMenuClose();
              onDelete();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Excluir
          </MenuItem> */}
        </Menu>
      </Box>

      <Box className={classes.cardContent}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="textSecondary">
            Número
          </Typography>
          <Typography variant="body2">
            {connection.number && connection.channel === "whatsapp"
              ? formatSerializedId(connection.number)
              : connection.number}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="textSecondary">
            Status
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {getStatusIcon(connection.status)}
            <Chip
              label={connection.status}
              size="small"
              className={classes.statusChip}
              sx={{
                backgroundColor: getStatusColor(connection.status).light,
                color: getStatusColor(connection.status).dark,
              }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="textSecondary">
            Última atualização
          </Typography>
          <Typography variant="body2">
            {format(parseISO(connection.updatedAt), "dd/MM/yy HH:mm")}
          </Typography>
        </Stack>

        {connection.isDefault && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Conexão Padrão"
            size="small"
            color="primary"
            variant="outlined"
          />
        )}

        <Divider />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {connection.status === "qrcode" && connection.channel === "whatsapp" && (
            <Can
              role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
              perform="connections-page:addConnection"
              yes={() => (
                <Button
                  variant="contained"
                  size="small"
                  onClick={onQrCode}
                  className={classes.actionButton}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  Ver QR Code
                </Button>
              )}
            />
          )}

          {connection.status === "DISCONNECTED" && connection.channel === "whatsapp" && (
            <Can
              role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
              perform="connections-page:addConnection"
              yes={() => (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onStartSession}
                    className={classes.actionButton}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": { backgroundColor: theme.palette.primary.dark },
                    }}
                  >
                    Reconectar
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onRequestNewQrCode}
                    className={classes.actionButton}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": { backgroundColor: theme.palette.primary.dark },
                    }}
                  >
                    Novo QR Code
                  </Button>
                </>
              )}
            />
          )}

          {(connection.status === "CONNECTED" ||
            connection.status === "PAIRING" ||
            connection.status === "TIMEOUT") &&
            connection.channel === "whatsapp" && (
              <Can
                role={user.profile}
                perform="connections-page:addConnection"
                yes={() => (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={onDisconnect}
                      className={classes.actionButton}
                    >
                      Desconectar
                    </Button>
                    {connection.importOldMessages && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={onClosedImported}
                        className={classes.actionButton}
                      >
                        Fechar Importados
                      </Button>
                    )}
                  </>
                )}
              />
            )}
        </Stack>
      </Box>
    </Card>
  );
};

const Connections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [planConfig, setPlanConfig] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const { getPlanCompany: originalGetPlanCompany } = usePlans();

  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

  // Estado e funções para o menu de nova conexão
  const [connectionMenuAnchorEl, setConnectionMenuAnchorEl] = useState(null);
  const handleOpenConnectionMenu = (event) => {
    setConnectionMenuAnchorEl(event.currentTarget);
  };
  const handleCloseConnectionMenu = () => {
    setConnectionMenuAnchorEl(null);
  };

  // Funções para login via Facebook/Instagram
  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then(() => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then(() => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  // Correção: o useEffect de plano depende somente de companyId
  useEffect(() => {
    async function fetchData() {
      try {
        const planConfigs = await originalGetPlanCompany(undefined, companyId);
        setPlanConfig(planConfigs);
      } catch (error) {
        toastError(error);
      }
    }
    fetchData();
  }, [companyId]);

  // useEffect para acompanhar o progresso da importação
  useEffect(() => {
    const importMessageHandler = (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    };
    socket.on(`importMessages-${user.companyId}`, importMessageHandler);
    return () => {
      socket.off(`importMessages-${user.companyId}`, importMessageHandler);
    };
  }, [socket, user.companyId, history]);

  // Dentro do useEffect que monitora as atualizações de sessão
  useEffect(() => {
    if (!user.companyId || !socket) return;

    const companyId = user.companyId;
    const onWhatsappSession = (data) => {
      if (data.action === "update") {
        if (data.session?.status === "DISCONNECTED" && data.error?.message) {
          toast.warning(data.error.message, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            theme: "colored",
          });
        }
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappSession);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappSession);
    };
  }, [socket, user.companyId]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    handleCloseConnectionMenu();
  };

  const handleOpenPrivacyWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setPrivacyModalOpen(true);
  };

  const handleClosePrivacyWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setPrivacyModalOpen(false);
  };

  const handleCloseWhatsAppModal = () => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  };

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = () => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  };

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.disconnected"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }
    setConfirmModalInfo(confirmationModalInitialState);
    setConfirmModalOpen(false);
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

    if (
    user.profile !== "admin" &&
    !(user.profile === "user" && user.allowConnections === "enabled")
  ) {
    return <ForbiddenPage />;
  }

  // Agrupa conexões por canal
  const groupedConnections = whatsApps.reduce((acc, connection) => {
    const channel = connection.channel || "other";
    if (!acc[channel]) acc[channel] = [];
    acc[channel].push(connection);
    return acc;
  }, {});

  if (groupedConnections.whatsapp) {
    groupedConnections.whatsapp.sort((a, b) => {
      if (a.status === "CONNECTED" && b.status !== "CONNECTED") return -1;
      if (b.status === "CONNECTED" && a.status !== "CONNECTED") return 1;
      return a.name.localeCompare(b.name);
    });
  }

  const channelConfig = {
    whatsapp: {
      label: "WhatsApp",
      icon: <WhatsApp style={{ color: "#25D366", marginRight: theme.spacing(1) }} />,
    },
    facebook: {
      label: "Facebook",
      icon: <Facebook style={{ color: "#3b5998", marginRight: theme.spacing(1) }} />,
    },
    instagram: {
      label: "Instagram",
      icon: <Instagram style={{ color: "#e1306c", marginRight: theme.spacing(1) }} />,
    },
  };

  const channelOrder = ["whatsapp", "facebook", "instagram"];

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>

      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && !privacyModalOpen && selectedWhatsApp?.id}
        />
      )}

      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && !privacyModalOpen && selectedWhatsApp?.id}
      />

      <PrivacyModal
        open={privacyModalOpen}
        onClose={handleClosePrivacyWhatsAppModal}
        whatsAppId={!qrModalOpen && !whatsAppModalOpen && selectedWhatsApp?.id}
      />

      <MainHeader>
        <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
          <Title>Conexões ({whatsApps.length})</Title>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
              startIcon={<RefreshIcon />}
              onClick={restartWhatsapps}
            >
              Reiniciar Conexões
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
              startIcon={<SupportIcon />}
              onClick={() =>
                window.open(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`, "_blank")
              }
            >
              Suporte
            </Button>
            <Can
              role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
              perform="connections-page:addConnection"
              yes={() => (
                <>
                  {/* Ao clicar, abre o menu com as opções */}
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": { backgroundColor: theme.palette.primary.dark },
                    }}
                    startIcon={<AddIcon />}
                    onClick={handleOpenConnectionMenu}
                  >
                    Nova Conexão
                  </Button>
                  <Menu
                    anchorEl={connectionMenuAnchorEl}
                    open={Boolean(connectionMenuAnchorEl)}
                    onClose={handleCloseConnectionMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem onClick={handleOpenWhatsAppModal}>
                      <WhatsApp style={{ color: "#25D366", marginRight: theme.spacing(1) }} />
                      WhatsApp
                    </MenuItem>
                    <FacebookLogin
                      appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                      autoLoad={false}
                      fields="name,email,picture"
                      version="21.0"
                      scope={
                        process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                          ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                          : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                      }
                      callback={responseFacebook}
                      render={(renderProps) => (
                        <MenuItem disabled={!planConfig?.plan?.useFacebook} onClick={renderProps.onClick}>
                          <Facebook style={{ color: "#3b5998", marginRight: theme.spacing(1) }} />
                          Facebook
                        </MenuItem>
                      )}
                    />
                    <FacebookLogin
                      appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                      autoLoad={false}
                      fields="name,email,picture"
                      version="21.0"
                      scope={
                        process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                          ? "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                          : "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                      }
                      callback={responseInstagram}
                      render={(renderProps) => (
                        <MenuItem disabled={!planConfig?.plan?.useInstagram} onClick={renderProps.onClick}>
                          <Instagram style={{ color: "#e1306c", marginRight: theme.spacing(1) }} />
                          Instagram
                        </MenuItem>
                      )}
                    />
                  </Menu>
                </>
              )}
            />
          </Stack>
        </Stack>
      </MainHeader>

      {statusImport?.all && (
        <Paper className={classes.importProgress} elevation={0} sx={{ backgroundColor: theme.palette.background.paper }}>
          <Stack spacing={2}>
            <Typography variant="h6">
              {statusImport?.this === -1 ? "Preparando importação..." : "Importando mensagens"}
            </Typography>
            {statusImport?.this === -1 ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack spacing={1}>
                <Typography variant="body1" align="center">
                  {`Processados ${statusImport?.this} de ${statusImport?.all} | Data: ${statusImport?.date}`}
                </Typography>
                <Box display="flex" justifyContent="center">
                  <CircularProgress
                    variant="determinate"
                    value={(statusImport?.this / statusImport?.all) * 100}
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      <Paper className={classes.mainContainer} elevation={0} sx={{ backgroundColor: theme.palette.background.default }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {channelOrder.map((channelType) => {
              const config = channelConfig[channelType];
              const connections = groupedConnections[channelType] || [];
              if (connections.length === 0) return null;
              return (
                <Box key={channelType}>
                  <Stack direction="row" alignItems="center" className={classes.sectionTitle}>
                    {config.icon}
                    <Typography variant="h6" component="h2">
                      {config.label} ({connections.length})
                      {channelType === "whatsapp" && (
                        <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                          ({connections.filter((c) => c.status === "CONNECTED").length} ativos)
                        </Typography>
                      )}
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {connections.map((connection) => (
                      <Grid item xs={12} sm={6} md={4} key={connection.id}>
                        <ConnectionCard
                          connection={connection}
                          onEdit={() => handleEditWhatsApp(connection)}
                          onDelete={() => handleOpenConfirmationModal("delete", connection.id)}
                          onQrCode={() => handleOpenQrModal(connection)}
                          onPrivacy={() => handleOpenPrivacyWhatsApp(connection)}
                          onDisconnect={() => handleOpenConfirmationModal("disconnect", connection.id)}
                          onStartSession={() => handleStartWhatsAppSession(connection.id)}
                          onRequestNewQrCode={() => handleRequestNewQrCode(connection.id)}
                          onClosedImported={() => handleOpenConfirmationModal("closedImported", connection.id)}
                          onDoubleClick={handleEditWhatsApp}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Connections;
